/**
 * SKU Master Data Auditor — single source of truth for every number shared
 * across this set's surfaces (chat script, insight, mock agent). Every figure
 * the script or insight prints imports from here; never inline a shared number.
 *
 * SHARED WORLD: this is the catalog / master-data chair of the same fictional
 * company as `sets/cascade-3pl` (ops) and `sets/invoice-audit` (finance) —
 * Cascade 3PL, ~$165M/yr revenue, 4 FCs (Indianapolis HQ, Atlanta, Reno,
 * Charlotte), ~280 FTE, 68 DTC brands, ShipHero + NetSuite + Google Workspace
 * + Slack; carriers FedEx / UPS / USPS / OnTrac.
 *
 * MONEY FIREWALL — three distinct money concepts live in this world; never
 * let them blur:
 *   1. invoice-audit *leakage* — carrier billed WRONG vs contract; disputable.
 *      ~$84K/mo · ~$1.0M/yr · $62K expired.
 *   2. cascade-3pl carrier *claims* — damage/loss dollars; ~$11.8K/wk.
 *   3. THIS set's *absorbed rating deltas* — carrier billed CORRECTLY (re-dim /
 *      re-weigh on actuals), but Cascade rated its brands off bad master data
 *      and ate the difference. NOT disputable — only fixable upstream, in the
 *      catalog. ~$63K/mo run-rate (deliberately ≠ invoice-audit's $62K).
 *
 * Math audit (all rows reconcile — see docs/demos/sku-master-data-auditor.md §1):
 *   census        3,720 + 1,460 + 840 + 68 = 6,088 flagged (4.9% of 124,600)
 *   sourcing      2,150 + 3,100 + 838       = 6,088
 *   by mechanism  A $154K + B $22K + C $13K = $189K / 90d
 *     A head      73,700 parcels × $1.71 = ~$126K
 *     A tail      22,000 parcels × $1.27 = ~$28K   (126 + 28 = 154)
 *     B rework    3,550 events × $6.20    = ~$22K
 *     C intl      1,600 parcels × $8.10   = ~$13K
 *   by category   dims $130K + weights $39K + HS $20K = $189K (hazmat = —)
 *   run-rate      $189K ÷ 3 = ~$63K/mo · × 12 = ~$756K/yr
 *     cross-check $756K / $165M rev = 0.46% · $756K / $72M carrier spend = 1.05%
 *   Pareto head   top 214 SKUs = $126K = 82% of the $154K rating-delta pool
 *     head avg    $126K ÷ 214 = ~$589/SKU · hero $4,182 ≈ 7.1× head avg
 *   hero DIM      26×14×12 = 4,368 cu-in ÷ 139 = 31.4 → billed 32 lb
 *                 rated ~$9.20 (2 lb) vs billed ~$19.40 → $10.20 absorbed/parcel
 *                 × 410 re-dimmed of 460 shipped = $4,182 / 90d
 */

// ---------------------------------------------------------------------------
// World anchor (agrees with cascade-3pl + invoice-audit)
// ---------------------------------------------------------------------------

export const WORLD = {
  annualRevenue: 165_000_000,
  annualCarrierSpend: 72_000_000,
  brands: 68,
  facilities: 4,
  /** Physical parcels shipped in the 90-day window. Same base as
   *  invoice-audit's SWEEP_90D by construction — different unit: parcels
   *  here, invoice line-items there. */
  parcels90d: 2_400_000,
} as const

// ---------------------------------------------------------------------------
// Beat 1 — the census (catalog export in; catalog-only, no carrier data yet)
// ---------------------------------------------------------------------------

export const CENSUS = {
  catalogFileName: 'Cascade_SKU_Catalog_Export_2026-07.csv',
  skusScanned: 124_600,
  flagged: 6_088,
  flaggedPct: 4.9,
  /** The velocity hook — most flagged SKUs are dormant, so count ≠ cost. */
  shippedIn90d: 2_340,
  dormantPct: 62,
} as const

export type DefectCategoryRow = {
  id: string
  label: string
  /** Total SKUs flagged in this (disjoint) category. */
  skuCount: number
  /** How many of those actually shipped in the last 90 days. */
  shipped90d: number
  /** Severity drives the row accent. Volume-tier for dims/weights, risk-tier
   *  (critical) for hazmat regardless of count. */
  status: 'critical' | 'warning' | 'neutral'
}

/** Disjoint categories; each SKU counted once in its worst category.
 *  skuCount column sums to CENSUS.flagged (6,088). */
export const DEFECT_CATEGORIES: DefectCategoryRow[] = [
  {
    id: 'weights',
    label: 'Missing / zero weight',
    skuCount: 3_720,
    shipped90d: 1_190,
    status: 'warning',
  },
  {
    id: 'dims',
    label: 'Placeholder / implausible dims',
    skuCount: 1_460,
    shipped90d: 910,
    status: 'warning',
  },
  {
    id: 'hs',
    label: 'Missing HS code (ships internationally)',
    skuCount: 840,
    shipped90d: 210,
    status: 'neutral',
  },
  {
    id: 'hazmat',
    label: 'Un-flagged hazmat signature',
    skuCount: 68,
    shipped90d: 30,
    status: 'critical',
  },
]

/** Hazmat is a compliance lane — never a dollar line. Counts + exposure only. */
export const HAZMAT = {
  skuCount: 68,
  lithium: 41,
  aerosol: 19,
  flammable: 8,
  parcels90d: 2_900,
} as const

// ---------------------------------------------------------------------------
// Hero SKU — the worked example both evidence drills use
// ---------------------------------------------------------------------------

export const HERO_SKU = {
  sku: 'FW-DUF-0288',
  brand: 'Fernweh Outdoors',
  product: 'Basecamp Duffel 90L',
  // Beat 1 catalog-only tell:
  masterDims: '12×9×3 in',
  masterWeight: '2.0 lb',
  titleLiters: 90,
  masterLiters: 5.3, // 12×9×3 = 324 cu-in × 0.016387 = 5.31 L
  // Beat 2 carrier reveal:
  measuredDims: '26×14×12 in',
  measuredWeight: '6.8 lb',
  dimCubic: 4_368, // 26×14×12
  dimDivisor: 139,
  dimBilledLb: 32, // 4,368 ÷ 139 = 31.4 → rounds up to 32
  ratedCost: 9.2, // Cascade rated the brand at 2 lb Ground
  billedCost: 19.4, // carrier billed the 32 lb DIM weight
  absorbedPerParcel: 10.2, // 19.40 − 9.20
  shipped90d: 460,
  reDimmed90d: 410,
  absorbed90d: 4_182, // 410 × $10.20
} as const

// ---------------------------------------------------------------------------
// Beat 2 — the dollars (ShipHero billing export joined)
// ---------------------------------------------------------------------------

export const BILLING = {
  billingFileName: 'Cascade_ShipHero_Billing_Export_Q2-2026.csv',
  /** Carrier-adjustment lines carried alongside order + SKU refs in the
   *  ShipHero export (5.3% of 2.4M parcels). */
  adjustmentLines: 128_400,
  /** Adjustment lines that trace to a flagged SKU (75%). */
  linesToFlagged: 95_700,
  /** Only single-SKU shipments were priced (unambiguous attribution) — so the
   *  total is a conservative floor. */
  singleSkuSharePct: 86,
} as const

/** 90-day absorbed cost by cost MECHANISM (the evidence). Sums to $189K. */
export const ABSORBED = {
  total90d: 189_000,
  monthlyRunRate: 63_000, // 189 ÷ 3 (deliberately ≠ invoice-audit's $62K)
  annualized: 756_000, // 63 × 12
  revenuePct: 0.46, // 756K / 165M
  carrierSpendPct: 1.05, // 756K / 72M
  // Mechanism A — absorbed DIM/weight rating deltas
  ratingDeltas: 154_000,
  headParcels: 73_700,
  headDeltaPerParcel: 1.71, // 73,700 × 1.71 = ~$126K
  headDeltas: 126_000,
  tailParcels: 22_000,
  tailDeltaPerParcel: 1.27, // 22,000 × 1.27 = ~$28K
  tailDeltas: 28_000,
  // Mechanism B — mis-pick / cartonization rework
  reworkEvents: 3_550,
  reworkPerEvent: 6.2,
  reworkTotal: 22_000,
  // Mechanism C — international holds & brokerage re-files
  intlParcels: 1_600,
  intlPerParcel: 8.1,
  intlTotal: 13_000,
} as const

/** 90-day absorbed cost by defect CATEGORY (the rank-flip — mechanisms A–C
 *  allocated to their driving category). dollars column sums to $189K.
 *  The count column is intentionally non-monotonic vs dollars: the point. */
export type CategoryDollarsRow = {
  id: string
  label: string
  skuCount: number
  /** 90-day absorbed dollars driven by this category. Null renders as "—". */
  dollars90d: number | null
  status: 'critical' | 'warning' | 'neutral'
}

// Status mirrors DEFECT_CATEGORIES (census) so a category never flips color
// between the two tables shown back-to-back. Hazmat is the one intentional
// exception: critical in the census (compliance risk lane) but neutral here —
// it carries no dollars, so it must not read as the top dollar row. The
// dollar story is carried by rank + the emphasized row, not the accent.
export const CATEGORY_DOLLARS: CategoryDollarsRow[] = [
  {
    id: 'dims',
    label: 'Placeholder / implausible dims',
    skuCount: 1_460,
    dollars90d: 130_000,
    status: 'warning',
  },
  {
    id: 'weights',
    label: 'Missing / zero weight',
    skuCount: 3_720,
    dollars90d: 39_000,
    status: 'warning',
  },
  {
    id: 'hs',
    label: 'Missing HS code',
    skuCount: 840,
    dollars90d: 20_000,
    status: 'neutral',
  },
  {
    id: 'hazmat',
    label: 'Un-flagged hazmat signature',
    skuCount: 68,
    dollars90d: null, // compliance lane — never a dollar line
    status: 'neutral',
  },
]

// ---------------------------------------------------------------------------
// Pareto head — the dollar-ranked fix list (mechanism-A rating deltas only).
// The visible $ column sums to $126K across ALL 214 rows (82% of the $154K
// rating-delta pool). NEVER print "81% of total" or "$151K" against this
// column — $151K is the all-in head (incl. ~$25K allocated mis-pick/customs)
// and does not reconcile against the visible dollars.
// ---------------------------------------------------------------------------

export const FIX_LIST = {
  headSkuCount: 214,
  headDollars: 126_000, // = ABSORBED.headDeltas
  headSharePct: 82, // 126 / 154 of the rating-delta pool
  headAvgPerSku: 589, // 126,000 / 214
  /** ~$25K of mis-pick + intl (mechanisms B/C) allocates to the head SKUs;
   *  quoted as all-in context, NEVER against the $ column. */
  allocatedReworkIntl: 25_000,
} as const

export type FixListRow = {
  sku: string
  brand: string
  defect: string
  /** Parcels re-dimmed / re-weighed by the carrier in 90 days. */
  units90d: number
  /** Mechanism-A absorbed rating delta over 90 days. */
  absorbed90d: number
  emphasize?: boolean
}

/** Top rows of the 214-SKU fix list, ranked strictly by absorbed dollars.
 *  Displayed rows do NOT sum to $126K — the footer states the full 214 total.
 *  Per-SKU dollars decay believably from the hero's $4,182 toward the ~$589
 *  head average. Brands drawn from the Cascade roster; each defect type shown
 *  once; hero (Fernweh) is row 1. */
export const FIX_LIST_ROWS: FixListRow[] = [
  {
    sku: HERO_SKU.sku,
    brand: HERO_SKU.brand,
    defect: 'Placeholder dims (12×9×3 → 26×14×12)',
    units90d: HERO_SKU.reDimmed90d,
    absorbed90d: HERO_SKU.absorbed90d,
    emphasize: true,
  },
  {
    sku: 'HP-CRATE-1140',
    brand: 'Halcyon Pet',
    defect: 'Placeholder dims (1×1×1 default)',
    units90d: 950,
    absorbed90d: 2_880,
  },
  {
    sku: 'ALT-TUB-0662',
    brand: 'Altitude Supplements',
    defect: 'Missing weight (defaulted 1.0 lb)',
    units90d: 810,
    absorbed90d: 1_940,
  },
  {
    sku: 'OAK-SET-0391',
    brand: 'OakHill Skincare',
    defect: 'Variant-copied dims (bundle)',
    units90d: 700,
    absorbed90d: 1_510,
  },
  {
    sku: 'TRM-BULK-0208',
    brand: 'Trailmix Provisions',
    defect: 'Missing weight (defaulted 1.0 lb)',
    units90d: 760,
    absorbed90d: 1_180,
  },
  {
    sku: 'BRR-BOX-0774',
    brand: 'Briar Coffee Roasters',
    defect: 'Placeholder dims (density outlier)',
    units90d: 700,
    absorbed90d: 940,
  },
]

// ---------------------------------------------------------------------------
// Beat 2 — the billing-model finding + the root-cause pattern
// ---------------------------------------------------------------------------

export const BILLING_MODEL = {
  /** Share of the 128,400 adjustment lines that trace to Cascade's own bad
   *  SKU data (carrier billed correctly). The mirror image of invoice-audit. */
  ownDataSharePct: 75,
} as const

export const ROOT_CAUSE = {
  /** Share of placeholder dims that trace to four Feb–Apr catalog imports. */
  importSharePct: 62,
  importCount: 4,
  /** Largest single source — an EXISTING brand's seasonal line refresh, never
   *  "brand onboarding". Deliberately NOT Northwind (which owns the ops
   *  margin story + invoice-audit's address-feed root cause). */
  largestSource: 'Fernweh Outdoors',
  largestImport: "Fernweh's February catalog import (spring line)",
} as const

// ---------------------------------------------------------------------------
// Beat 3 — the corrected-catalog draft (sourcing / confidence split)
// ---------------------------------------------------------------------------

export const DRAFT = {
  /** A Google Sheet Neo creates in Drive (not an uploaded .xlsx) — shared with
   *  the catalog lead + FC leads for review. */
  fileName: 'Cascade SKU Catalog — Corrected (DRAFT)',
  /** Corrections sourced from carrier-billed actuals (high confidence). Capped
   *  by the 2,340 flagged SKUs that actually shipped in-window (measured
   *  truth), so ≤ CENSUS.shippedIn90d. */
  fromActuals: 2_150,
  /** Inferred from variant siblings / catalog plausibility (medium). */
  fromInference: 3_100,
  /** No source — needs re-measure / cubiscan (flagged, not filled). */
  needsRemeasure: 838, // 2,150 + 3,100 + 838 = 6,088
} as const

// ---------------------------------------------------------------------------
// Beat 4 — two-path decision + the standing SKU Intake Auditor
// ---------------------------------------------------------------------------

export const PATHS = {
  /** Both paths stop the same ~$756K/yr now. */
  stoppedAnnual: 756_000,
  // Path A — fix once
  newSkusPerMonth: 1_540,
  historicalDefectRatePct: 4.9,
  newDefectsPerMonth: 75, // 1,540 × 4.9%
  regrownMonthlyByYearEnd: 23_000, // leak regrows to ~$23K/mo within a year
  reabsorbedYearOne: 140_000,
  // Path B — fix + intake auditor
} as const

// ---------------------------------------------------------------------------
// Agents tab — the one standing "SKU Intake Auditor — weekly" job.
// Per-run numbers derive from the regrowth math: ~1,540 new/changed SKUs/mo
// ≈ ~356/wk checked; ~75 defective/mo ≈ ~17/wk queued as corrections.
// ---------------------------------------------------------------------------

export const INTAKE_AUDITOR = {
  jobTitle: 'SKU Intake Auditor — weekly',
  skusCheckedPerRunLow: 350,
  skusCheckedPerRunHigh: 400,
  correctionsPerRunLow: 15,
  correctionsPerRunHigh: 20,
  deliveryChannel: '#catalog-ops',
} as const

// ---------------------------------------------------------------------------
// Prospective chat-hero insight — benchmark RANGES only. Neo has NOT audited
// Cascade's catalog yet, so the hero frames the *expected* problem, never the
// Beat 1–2 reveals ($189K / $63K / 6,088 / 214). Ranges are industry
// benchmarks, not results.
// ---------------------------------------------------------------------------

export const BENCHMARK = {
  defectRateLowPct: 3,
  defectRateHighPct: 6,
  parcelsPerMonth: 800_000, // WORLD.parcels90d ÷ 3
  absorbedAnnualLow: 500_000,
  absorbedAnnualHigh: 900_000,
} as const
